import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { Playlist } from 'types/playlist'
import type { State } from 'types/redux'
import { useTranslation } from '@disco/i18n/use-translation'
import {
  loadPlaylist,
  updatePlaylist,
} from '@disco/state-management/actions/playlist-actions'
import { getPlaylist } from '@disco/state-management/selectors/playlists'
import PlaylistGridItem from 'components/common/playlist-grid-item'
import Row from 'components/common/row'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import ChoiceList from 'components/ui/choice-list'
import ColorInput from 'components/ui/color-input'
import FileSelector from 'components/ui/file-selector'
import Grid from 'components/ui/grid'
import Label from 'components/ui/label'
import SidebarModal from 'components/ui/sidebar-modal'

const getSelectedView = (playlist: Playlist) => {
  if (playlist['color']) {
    return 'color'
  }

  if (playlist['image_url']) {
    return 'image'
  }

  return 'default'
}

type Props = {
  backPath?: string
}

/**
 * Modal for editing a playlist's cover design.
 * @param backPath - Where to go on close; defaults to the catalog's playlists page
 */
const LibrariesLibraryPlaylistsPlaylist = ({
  backPath: backPathProp,
}: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const params = useParams<{ playlistId: string; libraryId: string }>()
  const { t } = useTranslation('catalogs-management')
  const playlistId: number = parseInt(params.playlistId!, 10)
  const playlist: Playlist = useSelector((state: State) =>
    getPlaylist(state, {
      id: playlistId,
    }),
  )
  useEffect(() => {
    dispatch(loadPlaylist(playlistId))
  }, [playlistId])
  const [selectedView, setSelectedView] = useState(getSelectedView(playlist))
  const [color, setColor] = useState(
    playlist && playlist['color'] ? playlist['color'] : '#00D084',
  )
  const [image, setImage] = useState<File | null | undefined>(null)
  const [imageUrl, setImageUrl] = useState<string | null | undefined>(null)

  // useEffect(() => {
  //   if (playlist) {
  //     setSelectedView(getSelectedView(playlist))
  //     if (playlist['color']) {
  //       setColor(playlist['color'])
  //     }
  //   }
  // }, [playlist])
  const handleImageChange = (files: File[] | null | undefined) => {
    const file = files ? files[0] : null
    setImage(file)
  }

  const backPath = backPathProp ?? `/catalogs/${params.libraryId}/playlists`

  const handleCancel = () => navigate(backPath)

  const handleSubmit = (event: React.SyntheticEvent<any>) => {
    event.preventDefault()
    dispatch(
      updatePlaylist(playlistId, {
        color: ['default', 'image'].includes(selectedView) ? null : color,
        image: ['default', 'color'].includes(selectedView) ? null : image,
      }),
    )
    navigate(backPath)
  }

  const renderContent = () => {
    if (selectedView === 'default') {
      return <PlaylistGridItem color={null} image={null} playlist={playlist} />
    }

    if (selectedView === 'color') {
      return (
        <>
          <Row columnsSize={[12]}>
            <div>
              <Label
                caption={t(
                  'catalogs-management:selectColorForPlaylistCover',
                  'Select a color for your playlist cover',
                )}
              />
              <ColorInput
                name="style.color"
                value={color}
                onChange={color => setColor(color.hex)}
              />
            </div>
          </Row>
          <Row columnsSize={[12]}>
            <div>
              <PlaylistGridItem
                color={color}
                image={null}
                playlist={playlist}
              />
            </div>
          </Row>
        </>
      )
    }

    if (selectedView === 'image') {
      return (
        <>
          <Row columnsSize={[12]}>
            <div>
              <Label
                caption={
                  <>
                    {t(
                      'catalogs-management:selectImageForPlaylistCover',
                      'Select an image for your playlist cover',
                    )}
                    <br />
                    {t(
                      'catalogs-management:recommendedSize',
                      '(recommended size: {{size}})',
                      { size: '1500 x 1000px' },
                    )}
                  </>
                }
              />
              <FileSelector
                caption={[
                  t(
                    'catalogs-management:dragOrBrowseImages',
                    'Drag new or existing images here, or click to browse your computer',
                  ),
                  t(
                    'catalogs-management:clickToChangeImage',
                    'Click to change image',
                  ),
                ]}
                image={playlist && playlist['image_url']}
                showPreview
                type="image"
                onDrop={handleImageChange}
                onPreview={setImageUrl}
                height={132}
                dnd
              />
            </div>
          </Row>
          <Row columnsSize={[12]}>
            <div>
              <PlaylistGridItem
                color={null}
                image={imageUrl || (playlist && playlist['image_url']) || null}
                playlist={playlist}
              />
            </div>
          </Row>
        </>
      )
    }

    return null
  }

  return (
    <SidebarModal
      title={t('catalogs-management:playlistDesign', 'Playlist design')}
      sidebar={
        <ChoiceList
          title={t(
            'catalogs-management:chooseDesignViewForPlaylist',
            'Choose design view for playlist',
          )}
          multiple={false}
          items={[
            {
              label: t('catalogs-management:default', 'Default'),
              value: 'default',
            },
            {
              label: t('catalogs-management:chooseAColor', 'Choose a color'),
              value: 'color',
            },
            {
              label: t('catalogs-management:chooseAnImage', 'Choose an image'),
              value: 'image',
            },
          ]}
          selected={selectedView}
          onChange={(value: string) => setSelectedView(value)}
        />
      }
      content={<Grid size="large">{renderContent()}</Grid>}
      actions={
        <ButtonContainer>
          <Button
            appearance="link"
            caption={t('catalogs-management:cancel', 'Cancel')}
            onClick={handleCancel}
          />
          <Button
            appearance="primary"
            caption={t('catalogs-management:confirm', 'Confirm')}
            onClick={handleSubmit}
          />
        </ButtonContainer>
      }
      onClose={handleCancel}
    />
  )
}

export default LibrariesLibraryPlaylistsPlaylist
